import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, CheckSquare, Square } from 'lucide-react';
import './ManagePopupFieldsDialog.css'; // Creeremo questo file per lo styling

interface ManagePopupFieldsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  layerId: string; // Potrebbe servire in futuro
  layerName: string;
  availableFields: string[];
  initialSelectedFields: string[] | null;
  onSave: (fields: string[] | null) => void;
}

const ManagePopupFieldsDialog: React.FC<ManagePopupFieldsDialogProps> = ({
  isOpen,
  onClose,
  layerId: _layerId,
  layerName,
  availableFields,
  initialSelectedFields,
  onSave,
}) => {
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());

  // Inizializza lo stato locale quando il dialog si apre o i campi iniziali cambiano
  useEffect(() => {
    if (isOpen) {
      if (initialSelectedFields === null) {
        // Se null, seleziona tutti i campi disponibili
        setSelectedFields(new Set(availableFields));
      } else {
        setSelectedFields(new Set(initialSelectedFields));
      }
    }
  }, [isOpen, initialSelectedFields, availableFields]);

  const handleCheckboxChange = (field: string) => {
    setSelectedFields((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(field)) {
        newSelected.delete(field);
      } else {
        newSelected.add(field);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    setSelectedFields(new Set(availableFields));
  };

  const handleDeselectAll = () => {
    setSelectedFields(new Set());
  };

  const handleSaveChanges = () => {
    const selectedArray = Array.from(selectedFields);
    // Se tutti i campi disponibili sono selezionati, salva null
    if (selectedArray.length === availableFields.length) {
      onSave(null);
    } else {
      onSave(selectedArray);
    }
    onClose(); // Chiudi il dialog dopo il salvataggio
  };

  if (!isOpen) {
    return null;
  }

  // Usa createPortal per renderizzare nel body
  return ReactDOM.createPortal(
    <div className="manage-fields-dialog-overlay">
      <div className="manage-fields-dialog">
        <div className="manage-fields-dialog-header">
          <h3>Gestisci Campi Popup per '{layerName}'</h3>
          <button onClick={onClose} className="close-button" aria-label="Chiudi">
            <X size={20} />
          </button>
        </div>
        <div className="manage-fields-dialog-content">
          <div className="dialog-actions top-actions">
             <button onClick={handleSelectAll} disabled={availableFields.length === 0}>
               Seleziona Tutti
             </button>
             <button onClick={handleDeselectAll} disabled={availableFields.length === 0}>
               Deseleziona Tutti
             </button>
          </div>
          <div className="fields-list-container">
            {availableFields.length > 0 ? (
              availableFields.map((field) => (
                <div key={field} className="field-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedFields.has(field)}
                      onChange={() => handleCheckboxChange(field)}
                      className="hidden-checkbox" // Nascondi checkbox nativa
                    />
                    {selectedFields.has(field) ? (
                      <CheckSquare size={18} className="checkbox-icon checked" />
                    ) : (
                      <Square size={18} className="checkbox-icon" /> /* Classe unchecked rimossa */
                    )}
                    <span className="field-name">{field}</span>
                  </label>
                </div>
              ))
            ) : (
              <p>Nessun campo disponibile per questo layer.</p>
            )}
          </div>
        </div>
        <div className="manage-fields-dialog-footer">
          <button onClick={onClose} className="cancel-button">
            Annulla
          </button>
          <button
            onClick={handleSaveChanges}
            className="save-button"
            disabled={availableFields.length === 0}
          >
            Salva
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ManagePopupFieldsDialog;